import { useState, useEffect } from 'react';
import { useMutation } from 'react-apollo';
import { useDispatch } from 'react-redux';

import useEmailPinAuth from '../../../../../../components/auth/hooks/useEmailPinAuth';
import usePhoneAuth from '../../../../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../../../../services/recaptcha/useGoogleRecaptcha';
import marketingAccountLinkMutation from '../mutations/marketing-account-link';
import { loginSuccess } from '../../../../../../actions/auth-actions';
import { AUTH_TYPE } from '../../../../../../constants/auth';
import { ACCOUNT_TYPE } from '../../../AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';

/**
 * Handles the pin based authentication for an account associated with a
 * roster QR code. Supports both email and phone contact methods.
 *
 * @param {Object} params
 * @param {Object} params.account The account to authenticate.
 * @param {('email'|'phone')} [params.method] The contact method to use. When
 *   omitted, email is preferred and phone is used as a fallback.
 * @param {String} [params.emailAddress] The specific email address to use.
 *   Accounts can have multiple email addresses, so this selects which one to
 *   authenticate with. Defaults to the first available email address.
 */
const useRosterQrCodeOnboarding = ({
  account,
  method,
  emailAddress: selectedEmailAddress,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const emailAddress = selectedEmailAddress ?? account?.email_addresses?.[0];
  const phoneNumber = account?.phone_number;
  // Honour an explicit method when provided, otherwise prefer email and fall
  // back to phone.
  const authMethodIsPhone = method
    ? method === AUTH_TYPE.PHONE
    : !emailAddress && !!phoneNumber;

  const { onEmailAuthenticationRequest, onLoginByEmail } = useEmailPinAuth();
  const {
    loading: phoneLoading,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();
  const { onTokenRequest } = useGoogleRecaptcha();
  const [marketingAccountLink] = useMutation(marketingAccountLinkMutation);

  // Links a marketing account to the freshly authenticated identity, mirroring
  // the behaviour of the MarketingAccountOnboardingView.
  const onMarketingAccountLink = async marketingAccountId => {
    await marketingAccountLink({
      variables: {
        input: {
          marketing_account_id: marketingAccountId,
        },
      },
    });
  };

  const requestPin = async () => {
    setLoading(true);
    setError(null);
    try {
      if (authMethodIsPhone) {
        const token = onTokenRequest({ action: 'login' });
        await onPhoneAuthSubmit(phoneNumber, true, token);
      } else if (emailAddress) {
        await onEmailAuthenticationRequest({ emailAddress });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailAddress || phoneNumber) {
      requestPin();
    }
  }, [emailAddress, phoneNumber, authMethodIsPhone]);

  const onPinVerify = async code => {
    setLoading(true);
    setError(null);
    try {
      if (authMethodIsPhone) {
        // Phone verification dispatches the authenticated state on success.
        await onPhoneAuthVerify(phoneNumber, code);
        return;
      }

      const result = await onLoginByEmail({ emailAddress, code });
      if (result?.data?.accountLoginByEmail?.success) {
        // When the authenticated account is a marketing account, link it to the
        // sign up before surfacing the authenticated state so the redirect to
        // the onboarding view only happens once linking has completed.
        if (account?.type === ACCOUNT_TYPE.MARKETING) {
          await onMarketingAccountLink(account.id);
        }
        dispatch(loginSuccess());
      }
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const onPinResend = async () => {
    setError(null);
    try {
      if (authMethodIsPhone) {
        const token = onTokenRequest({ action: 'login' });
        await onResendPhoneAuthCode(phoneNumber, true, token);
      } else if (emailAddress) {
        await onEmailAuthenticationRequest({ emailAddress });
      }
    } catch (e) {
      setError(e.message);
    }
  };

  return {
    loading: loading || phoneLoading,
    error,
    emailAddress,
    phoneNumber,
    authMethodIsPhone,
    onPinVerify,
    onPinResend,
    onMarketingAccountLink,
  };
};

export default useRosterQrCodeOnboarding;
